import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

export default function FeaturedRotator({ items = [], promos = [], onSelect, categoryName }) {
  const { lang, t, isRtl } = useLang();
  const [idx, setIdx] = useState(0);

  // Promotions (ad banners) appear first, then featured products — all in one carousel.
  const slides = useMemo(() => {
    const promoSlides = (promos || []).map((p) => ({ ...p, _type: "promo" }));
    const productSlides = (items || []).map((p) => ({ ...p, _type: "product" }));
    return [...promoSlides, ...productSlides];
  }, [promos, items]);

  useEffect(() => {
    setIdx(0);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const slide = slides[Math.min(idx, slides.length - 1)];

  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIdx((i) => (i + 1) % slides.length);

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-4 mb-12" data-testid="featured-rotator">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-[#0F5F63]">
          <Sparkles className="w-4 h-4" />
          <span className="text-xs uppercase tracking-[0.3em]">{t.featuredWeek}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={isRtl ? next : prev}
            data-testid="featured-prev"
            className="w-9 h-9 rounded-full border border-[#0F5F63]/20 hover:border-[#0F5F63] text-[#0F5F63] flex items-center justify-center transition-colors"
            aria-label="prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={isRtl ? prev : next}
            data-testid="featured-next"
            className="w-9 h-9 rounded-full border border-[#0F5F63]/20 hover:border-[#0F5F63] text-[#0F5F63] flex items-center justify-center transition-colors"
            aria-label="next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {slide._type === "promo" ? (
        <div
          data-testid={`promo-slide-${slide.id}`}
          className="relative w-full rounded-3xl overflow-hidden bg-[#F4F2EB]"
        >
          <div className="relative w-full aspect-[16/9] md:aspect-[21/8]">
            <img
              src={slide.image_url}
              alt={lang === "ar" ? slide.title_ar : slide.title_en}
              className="w-full h-full object-cover"
              key={slide.id}
            />
            {(slide.title_en || slide.title_ar || slide.subtitle_en || slide.subtitle_ar) && (
              <div
                className={`absolute inset-0 flex flex-col justify-end p-8 lg:p-12 bg-gradient-to-t from-black/70 via-black/20 to-transparent ${
                  isRtl ? "items-end text-right" : "items-start text-left"
                }`}
              >
                {(slide.title_en || slide.title_ar) && (
                  <h3 className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-3xl lg:text-5xl leading-tight font-semibold text-white drop-shadow`}>
                    {lang === "ar" ? slide.title_ar || slide.title_en : slide.title_en || slide.title_ar}
                  </h3>
                )}
                {(slide.subtitle_en || slide.subtitle_ar) && (
                  <p className="mt-3 text-white/90 max-w-xl leading-relaxed drop-shadow">
                    {lang === "ar" ? slide.subtitle_ar || slide.subtitle_en : slide.subtitle_en || slide.subtitle_ar}
                  </p>
                )}
              </div>
            )}
          </div>
          <Dots slides={slides} idx={idx} />
        </div>
      ) : (
        <button
          onClick={() => onSelect(slide)}
          data-testid={`featured-item-${slide.id}`}
          className="group block w-full text-start rounded-3xl overflow-hidden bg-[#F4F2EB] relative"
        >
          <div className="grid md:grid-cols-2">
            <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[380px] overflow-hidden">
              <img
                src={slide.image_url}
                alt={lang === "ar" ? slide.name_ar : slide.name_en}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                key={slide.id}
              />
            </div>
            <div className="p-8 lg:p-12 flex flex-col justify-center bg-[#0F5F63] text-[#FDFBF7]">
              <p className="text-[10px] uppercase tracking-[0.35em] opacity-80">
                {categoryName ? categoryName(slide.category) : slide.category}
              </p>
              <h3 className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} mt-4 text-4xl lg:text-5xl leading-[1.05] font-semibold`}>
                {lang === "ar" ? slide.name_ar : slide.name_en}
              </h3>
              <p className="mt-4 text-[#FDFBF7]/80 max-w-md leading-relaxed">
                {lang === "ar" ? slide.description_ar : slide.description_en}
              </p>
              <div className="mt-8 flex items-center gap-6">
                <div>
                  <p className="text-[10px] uppercase tracking-widest opacity-70">{t.price}</p>
                  <p className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-2xl mt-1`}>{slide.price} <span className="text-sm opacity-80">{t.currency}</span></p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest opacity-70">{t.calories}</p>
                  <p className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-2xl mt-1`}>{slide.calories} <span className="text-sm opacity-80">{t.kcal}</span></p>
                </div>
              </div>
            </div>
          </div>
          <Dots slides={slides} idx={idx} />
        </button>
      )}
    </section>
  );
}

function Dots({ slides, idx }) {
  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
      {slides.map((_, i) => (
        <span
          key={i}
          className={`h-1 rounded-full transition-all ${i === idx ? "w-8 bg-[#FDFBF7]" : "w-2 bg-[#FDFBF7]/50"}`}
        />
      ))}
    </div>
  );
}
